import React, { useCallback, useEffect } from "react";
import { AspectRatio, Box, Image, Text } from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { useFormatDateTime } from "@bookingcom/flights-core";

import { SPACER_PADDED, IMAGE_STATIC_ASSETS_URL } from "@flights/constants";
import { UICheapFlightComponent, UICheapFlightInfo } from "@flights/types";
import getRoundtripSearchUrl from "utils/search-urls";
import { getMarketingTrackingVariable } from "@flights/web-api-utils-universal";
import { getCurrencySymbolByPointOfSale } from "@flights/content-universal";
import styles from "./CheapFlightsItem.module.css";

import useGlobalContext from "hooks/useGlobalContext";
import useFormatPrice from "hooks/useFormatPrice";
import useCreateUrl from "hooks/useCreateUrl";
import useTrackFlightsWebUseOfferPos from "hooks/useTrackFlightsWebUseOfferPosExp";
import useLandingPageEvents from "hooks/c360/useLandingPageEvents";
import { stage, trackWithDefaultStage } from "@flights/et-universal";

interface Props {
  flight: UICheapFlightInfo;
  index: number;
  component: UICheapFlightComponent;
  isMobile?: boolean;
}

interface Image {
  src: string;
  alt: string;
}

const getImage = (toCityImageUrl: string, toCityName: string): Image => {
  return {
    src: `${IMAGE_STATIC_ASSETS_URL}${toCityImageUrl}`,
    alt: toCityName
  };
};

const getSearchUrl = (flight: UICheapFlightInfo): string => {
  return getRoundtripSearchUrl(
    {
      fromIata: flight.fromCityIata,
      toIata: flight.toCityIata,
      departureDate: flight.tripStartDate,
      returnDate: flight.tripEndDate
    },
    "flights_index_cf"
  );
};

const FlightsNearbyItem: React.FunctionComponent<Props> = ({ flight, index, component, isMobile }) => {
  const i18n = useI18n();
  const { formats } = useFormatDateTime(i18n);
  const { ipCountry } = useGlobalContext();
  const flightsWebUseOfferPosExp = useTrackFlightsWebUseOfferPos();

  const metaSalesCountry = flightsWebUseOfferPosExp.isVariant && getMarketingTrackingVariable("salesCountry");
  const { createUrlWithApiPrefix } = useCreateUrl();
  const { formatPrice } = useFormatPrice();
  const trackLandingPageEvents = useLandingPageEvents();

  const routeName = i18n.trans(
    t("seo_flights_new_index_lp_cheap_flights_meta_flight_route", {
      variables: { city_name_origin: flight.fromCityNameLocalised, city_name_destination: flight.toCityNameLocalised }
    })
  );

  const fromDateFormatted = formats.flightDate(flight.tripStartDate);
  const toDateFormatted = formats.flightDate(flight.tripEndDate);

  const isNoDateExperiment = isMobile
    ? trackWithDefaultStage("flights_web_cat_popular_nodate_mdot", 1)
    : trackWithDefaultStage("flights_web_cat_popular_nodate_www", 1);
  const flightParams =
    `${fromDateFormatted} - ${toDateFormatted}` +
    SPACER_PADDED +
    `${i18n.trans(t("seo_flights_new_index_lp_cheap_flights_meta_round_trip"))}`;

  // Optionally show price information.
  // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
  const currency = getCurrencySymbolByPointOfSale((metaSalesCountry || ipCountry).toLowerCase() as string);
  const fromPriceFormated = flight.price?.units
    ? formatPrice({ currencyCode: currency, units: flight.price?.units, nanos: 0 })
    : "";
  const getFromPrice = () =>
    `${i18n.trans(
      t("seo_flights_new_index_lp_cheap_flights_meta_flight_from_price", {
        variables: { flight_price: fromPriceFormated, num_exception: 1 }
      })
    )}`;

  const url = createUrlWithApiPrefix(getSearchUrl(flight));
  const handleClick = useCallback(() => {
    trackLandingPageEvents("click", component, { ...flight, position: index + 1, url });
  }, [component, flight, index, url, trackLandingPageEvents]);

  const imageUrl = !isMobile
    ? flight.toCityOtherImageSlugs?.["526x420:webp"]?.imageUrlSlug?.[0]
    : flight.toCityImageUrl;

  useEffect(() => {
    isMobile ? stage("flights_web_cat_popular_nodate_mdot", 2) : stage("flights_web_cat_popular_nodate_www", 2);
  }, [isMobile]);

  return (
    <a className={styles.anchorContainer} href={url} onClick={handleClick} onAuxClick={handleClick}>
      <Box padding={0}>
        <AspectRatio ratio="4:3">
          <Image
            className={styles.image}
            {...getImage(imageUrl || "", routeName)}
            fallback="image"
            fallbackImageSrc={`${IMAGE_STATIC_ASSETS_URL}${flight.toCityImageUrl}`}
          />
        </AspectRatio>
        <div className={styles.textContainer}>
          <Text className={styles.textLine} variant="strong_1">
            {routeName}
          </Text>
          {!isNoDateExperiment && (
            <Text className={styles.textLine} variant="body_2" color="neutral_alt">
              {flightParams}
            </Text>
          )}
          {fromPriceFormated && (
            <Text className={styles.textLine} variant="body_2" color="neutral_alt">
              {getFromPrice()}
            </Text>
          )}
        </div>
      </Box>
    </a>
  );
};
export default FlightsNearbyItem;
